import { getSeVolume } from "./mixSettings.js";
import { getReadyAudioContext } from "./audioContext.js";

type Tone = {
  frequency: number;
  at: number;
  duration?: number;
  gain?: number;
};

const SYNTH_VOLUME = 1.5;

function seGain(gain: number): number {
  return gain * SYNTH_VOLUME * getSeVolume();
}

function tone(audio: AudioContext, { frequency, at, duration = 0.16, gain = 0.16 }: Tone): void {
  const oscillator = audio.createOscillator();
  const envelope = audio.createGain();
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, at);
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(seGain(gain), at + 0.012);
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  oscillator.connect(envelope).connect(audio.destination);
  oscillator.start(at);
  oscillator.stop(at + duration + 0.02);
}

/** A dry mallet/electric-piano hybrid: clear attack, warm body, no generic notification feel. */
function workMallet(audio: AudioContext, { frequency, at, duration = 0.16, gain = 0.16 }: Tone): void {
  const output = audio.createGain();
  const filter = audio.createBiquadFilter();
  const body = audio.createOscillator();
  const brightness = audio.createOscillator();
  const envelope = audio.createGain();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(3800, at);
  filter.frequency.exponentialRampToValueAtTime(1300, at + duration);
  body.type = "sine";
  body.frequency.setValueAtTime(frequency, at);
  brightness.type = "triangle";
  brightness.frequency.setValueAtTime(frequency * 2, at);
  output.gain.setValueAtTime(0.7, at);
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(seGain(gain), at + 0.006);
  envelope.gain.exponentialRampToValueAtTime(seGain(gain) * 0.25, at + 0.045);
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  body.connect(envelope);
  brightness.connect(output).connect(envelope);
  envelope.connect(filter).connect(audio.destination);
  body.start(at);
  brightness.start(at);
  body.stop(at + duration + 0.02);
  brightness.stop(at + duration + 0.02);
}

/** The rare payoff sound: higher partials and a longer tail give it a glass-bell sheen. */
function glassBell(audio: AudioContext, { frequency, at, duration = 0.4, gain = 0.12 }: Tone): void {
  const output = audio.createGain();
  const partials = [1, 2.01, 3.18];
  const envelope = audio.createGain();
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(seGain(gain), at + 0.008);
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  output.connect(envelope).connect(audio.destination);
  partials.forEach((multiple, index) => {
    const oscillator = audio.createOscillator();
    oscillator.type = index === 0 ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(frequency * multiple, at);
    oscillator.connect(output);
    oscillator.start(at);
    oscillator.stop(at + duration + 0.02);
  });
  output.gain.setValueAtTime(0.72, at);
}

function stamp(audio: AudioContext, at: number): void {
  const oscillator = audio.createOscillator();
  const envelope = audio.createGain();
  oscillator.type = "triangle";
  oscillator.frequency.setValueAtTime(130, at);
  oscillator.frequency.exponentialRampToValueAtTime(72, at + 0.075);
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(seGain(0.22), at + 0.006);
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + 0.1);
  oscillator.connect(envelope).connect(audio.destination);
  oscillator.start(at);
  oscillator.stop(at + 0.12);
}

async function ready(): Promise<AudioContext> {
  const audio = await getReadyAudioContext();
  if (!audio) throw new Error("Web Audio is not supported");
  return audio;
}

function noise(audio: AudioContext, at: number, duration: number, gain: number, frequency: number): void {
  const source = audio.createBufferSource();
  const filter = audio.createBiquadFilter();
  const envelope = audio.createGain();
  const buffer = audio.createBuffer(1, Math.ceil(audio.sampleRate * duration), audio.sampleRate);
  const samples = buffer.getChannelData(0);
  samples.forEach((_, index) => { samples[index] = Math.random() * 2 - 1; });
  source.buffer = buffer;
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(frequency, at);
  filter.Q.setValueAtTime(1.4, at);
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(seGain(gain), at + 0.004);
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  source.connect(filter).connect(envelope).connect(audio.destination);
  source.start(at);
  source.stop(at + duration);
}

function sweep(audio: AudioContext, at: number, from: number, to: number, duration: number, gain: number): void {
  const oscillator = audio.createOscillator();
  const envelope = audio.createGain();
  oscillator.type = "triangle";
  oscillator.frequency.setValueAtTime(from, at);
  oscillator.frequency.exponentialRampToValueAtTime(to, at + duration);
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(seGain(gain), at + 0.01);
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  oscillator.connect(envelope).connect(audio.destination);
  oscillator.start(at);
  oscillator.stop(at + duration + 0.02);
}

export async function playUiSelect(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  workMallet(audio, { frequency: 880, at: now, duration: 0.075, gain: 0.075 });
}

export async function playUiConfirm(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  workMallet(audio, { frequency: 659.25, at: now, duration: 0.1, gain: 0.09 });
  workMallet(audio, { frequency: 987.77, at: now + 0.075, duration: 0.18, gain: 0.1 });
}

export async function playUiBlocked(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  sweep(audio, now, 185, 135, 0.11, 0.065);
}

export async function playStageStart(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  noise(audio, now, 0.06, 0.045, 1800);
  workMallet(audio, { frequency: 523.25, at: now + 0.045, duration: 0.16, gain: 0.1 });
  workMallet(audio, { frequency: 783.99, at: now + 0.13, duration: 0.25, gain: 0.11 });
}

export async function playCardDraw(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  noise(audio, now, 0.09, 0.035, 2500);
  workMallet(audio, { frequency: 1046.5, at: now + 0.03, duration: 0.09, gain: 0.055 });
}

export async function playCardUse(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  noise(audio, now, 0.055, 0.05, 1400);
  stamp(audio, now + 0.035);
}

/** A policy/action card activates with a deliberate, designed phrase rather than a card-drop sound. */
export async function playActionCardUse(count: 1 | 2 | 3 | 4 | 5): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  const notes = [783.99, 987.77, 1174.66, 1567.98, 2093];
  const frequency = notes[count - 1];
  sweep(audio, now, frequency * 0.58, frequency * 0.82, 0.06, 0.045 + count * 0.007);
  workMallet(audio, { frequency, at: now + 0.04, duration: 0.12 + count * 0.015, gain: 0.075 + count * 0.012 });
  if (count >= 2) workMallet(audio, { frequency: frequency * 1.25, at: now + 0.1, duration: 0.18, gain: 0.065 + count * 0.009 });
  if (count >= 4) glassBell(audio, { frequency: frequency * 1.5, at: now + 0.17, duration: 0.3, gain: 0.07 + count * 0.008 });
  if (count === 5) stamp(audio, now + 0.025);
}

/** A bright, complete little phrase rather than a generic rising UI sound. */
export async function playProgressPraise(level: 1 | 2 | 3 | 4): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.015;
  const melodies = {
    1: [659.25, 783.99], // E5 → G5
    2: [659.25, 783.99, 1046.5], // E5 → G5 → C6
    3: [523.25, 659.25, 783.99, 1046.5, 1318.51], // C major ascent
    4: [783.99, 1046.5, 1318.51, 1567.98, 2093, 2637.02], // bright octave leap
  } as const;
  melodies[level].forEach((frequency, index) => {
    workMallet(audio, { frequency, at: now + index * 0.095, duration: 0.17 + level * 0.025, gain: 0.1 + level * 0.016 });
  });
  if (level === 4) {
    glassBell(audio, { frequency: 3135.96, at: now + 0.51, duration: 0.46, gain: 0.12 });
    glassBell(audio, { frequency: 2093, at: now + 0.57, duration: 0.38, gain: 0.1 });
  }
}

/** Reserved for a genuinely exceptional result: 10+ progress in one resolution. */
export async function playBreakthroughPraise(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.015;
  stamp(audio, now);
  // A wide C-major arpeggio that climbs two octaves before the final glass burst.
  [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98, 2093].forEach((frequency, index) => {
    workMallet(audio, {
      frequency,
      at: now + 0.045 + index * 0.07,
      duration: 0.2 + index * 0.012,
      gain: 0.115 + index * 0.006,
    });
  });
  glassBell(audio, { frequency: 2637.02, at: now + 0.5, duration: 0.62, gain: 0.15 });
  glassBell(audio, { frequency: 3135.96, at: now + 0.56, duration: 0.55, gain: 0.11 });
  glassBell(audio, { frequency: 4186.01, at: now + 0.64, duration: 0.45, gain: 0.085 });
}

/** A bouncy office-like phrase: each action adds one note before the resolution. */
export async function playChainPraise(count: 2 | 3 | 4 | 5): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.015;
  const clickNotes = [587.33, 739.99, 880, 1174.66, 1469.98]; // D5 → F#5 → A5 → D6 → F#6
  Array.from({ length: count }).forEach((_, index) => {
    workMallet(audio, { frequency: clickNotes[index], at: now + index * 0.105, duration: 0.1, gain: 0.1 });
  });
  const resolveAt = now + count * 0.105;
  workMallet(audio, { frequency: count >= 4 ? 2349.32 : 1760, at: resolveAt, duration: 0.34, gain: 0.15 });
  if (count >= 4) {
    glassBell(audio, { frequency: 2936.65, at: resolveAt + 0.06, duration: 0.42, gain: 0.12 });
    glassBell(audio, { frequency: 3520, at: resolveAt + 0.13, duration: 0.28, gain: 0.08 });
  }
  if (count === 5) {
    stamp(audio, resolveAt - 0.01);
    glassBell(audio, { frequency: 4698.64, at: resolveAt + 0.2, duration: 0.42, gain: 0.09 });
  }
}

export async function playComebackPraise(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.015;
  tone(audio, { frequency: 196, at: now, duration: 0.13, gain: 0.11 });
  stamp(audio, now + 0.22);
  tone(audio, { frequency: 659.25, at: now + 0.3, duration: 0.2, gain: 0.14 });
  tone(audio, { frequency: 987.77, at: now + 0.39, duration: 0.42, gain: 0.16 });
}

export async function playFatigueHeal(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  glassBell(audio, { frequency: 783.99, at: now, duration: 0.26, gain: 0.07 });
  glassBell(audio, { frequency: 1046.5, at: now + 0.11, duration: 0.38, gain: 0.09 });
}

export async function playFatigueGain(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  sweep(audio, now, 260, 145, 0.22, 0.095);
  noise(audio, now + 0.03, 0.12, 0.035, 700);
}

export async function playDeadlineWarning(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  tone(audio, { frequency: 440, at: now, duration: 0.09, gain: 0.08 });
  tone(audio, { frequency: 440, at: now + 0.15, duration: 0.09, gain: 0.08 });
  tone(audio, { frequency: 370, at: now + 0.3, duration: 0.16, gain: 0.095 });
}

export async function playGoodEvent(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  glassBell(audio, { frequency: 659.25, at: now, duration: 0.2, gain: 0.07 });
  glassBell(audio, { frequency: 987.77, at: now + 0.1, duration: 0.34, gain: 0.1 });
}

export async function playBadEvent(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  noise(audio, now, 0.06, 0.04, 1300);
  sweep(audio, now + 0.02, 330, 105, 0.3, 0.09);
}

export async function playReward(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  workMallet(audio, { frequency: 659.25, at: now, duration: 0.14, gain: 0.08 });
  workMallet(audio, { frequency: 783.99, at: now + 0.09, duration: 0.14, gain: 0.09 });
  glassBell(audio, { frequency: 1318.51, at: now + 0.18, duration: 0.42, gain: 0.1 });
}

export async function playCaseSuccess(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  stamp(audio, now);
  [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
    workMallet(audio, { frequency, at: now + 0.08 + index * 0.09, duration: 0.19, gain: 0.1 });
  });
  glassBell(audio, { frequency: 1567.98, at: now + 0.43, duration: 0.55, gain: 0.14 });
}

/** A fixed victory melody reserved for clearing the final boss case. */
export async function playBossClear(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  stamp(audio, now);
  tone(audio, { frequency: 130.81, at: now, duration: 0.34, gain: 0.1 });
  // C major: a recognisable ascent, then a clear return to the tonic.
  [523.25, 659.25, 783.99, 1046.5, 1318.51, 1046.5, 783.99, 1046.5].forEach((frequency, index) => {
    workMallet(audio, {
      frequency,
      at: now + 0.07 + index * 0.16,
      duration: index === 7 ? 0.6 : 0.3,
      gain: 0.11,
    });
  });
  glassBell(audio, { frequency: 2093, at: now + 1.2, duration: 0.85, gain: 0.13 });
  glassBell(audio, { frequency: 2637.02, at: now + 1.28, duration: 0.76, gain: 0.1 });
}

export async function playCaseFailure(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  noise(audio, now, 0.12, 0.04, 850);
  sweep(audio, now, 220, 88, 0.42, 0.11);
  tone(audio, { frequency: 110, at: now + 0.28, duration: 0.35, gain: 0.08 });
}

/** A fixed, slow descending melody for a run-ending failure. */
export async function playGameOver(): Promise<void> {
  const audio = await ready();
  const now = audio.currentTime + 0.01;
  noise(audio, now, 0.1, 0.035, 620);
  // G minor: each note has room to land before the final low G.
  [392, 349.23, 311.13, 293.66, 261.63, 196].forEach((frequency, index) => {
    workMallet(audio, {
      frequency,
      at: now + index * 0.22,
      duration: index === 5 ? 0.82 : 0.34,
      gain: 0.09,
    });
  });
  tone(audio, { frequency: 98, at: now + 1.1, duration: 0.85, gain: 0.08 });
}
